import { useId, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Result, setFirmwareAsDesired } from '../../api/api';
import { Button } from '../Common/Button';
import { StatusField } from '../Common/StatusLabel';

type Params = {
  productCode: string;
  firmwareVersion: string;
  variant: string;
};

interface IProps {
  disabled?: boolean;
  maxDevices?: number;
  onSubmitted?: () => void;
}

export const PrepareListOfDevicesForFirmware = ({
  disabled = false,
  maxDevices,
  onSubmitted,
}: IProps) => {
  const {
    productCode,
    firmwareVersion,
    variant = '',
  } = useParams<Params>();

  const [serialNumbers, setSerialNumbers] = useState<string>('');
  const [firmwareAsDesiredResult, setFirmwareAsDesiredResult] =
    useState<Result<string>>({
      status: '',
    });
  const inputId = useId();
  const helpTextId = `${inputId}-help`;
  const validationId = `${inputId}-validation`;

  const parsedSerialNumbers = serialNumbers
    .trim()
    .split(/[\s,;]+/)
    .filter(Boolean);
  const validationMessage =
    parsedSerialNumbers.length === 0
      ? 'Enter at least one device serial number.'
      : maxDevices !== undefined &&
          parsedSerialNumbers.length > maxDevices
        ? `Enter ${maxDevices} serial numbers or fewer.`
        : undefined;
  const isValidSerialNumberList = validationMessage === undefined;

  const setAsDesiredFunc = async () => {
    if (
      disabled ||
      !isValidSerialNumberList ||
      firmwareAsDesiredResult.status === 'Loading'
    ) {
      return;
    }

    setFirmwareAsDesiredResult({ status: 'Loading' });
    const result = await setFirmwareAsDesired(
      Number(productCode),
      parsedSerialNumbers,
      Number(firmwareVersion),
      variant,
    );
    setFirmwareAsDesiredResult(result);
    if (result.status === 'OK') {
      onSubmitted?.();
    }
  };
  return (
    <div>
      <div className="flex align-middle content-center items-center">
        <div>
          <label
            htmlFor={inputId}
            className="block text-sm font-light mb-1"
          >
            Device serial numbers
          </label>
          <textarea
            id={inputId}
            disabled={
              disabled || firmwareAsDesiredResult.status === 'Loading'
            }
            aria-invalid={!isValidSerialNumberList}
            aria-describedby={`${helpTextId}${
              validationMessage ? ` ${validationId}` : ''
            }`}
            className="bg-purple-white rounded border-2 border-grey p-3"
            value={serialNumbers}
            onChange={(v) =>
              setSerialNumbers(v.target.value?.toUpperCase())
            }
            placeholder="EH062687, EH02321"
          />
          <div id={helpTextId} className="text-xs font-light mt-1">
            {maxDevices !== undefined
              ? `Up to ${maxDevices} serial numbers can be submitted before QA approval.`
              : 'Separate serial numbers with commas, spaces, semicolons, or new lines.'}
          </div>
        </div>
        <div className="pl-4">
          <Button
            onClick={setAsDesiredFunc}
            text={'Send'}
            disabled={[
              {
                disabled:
                  disabled ||
                  !isValidSerialNumberList ||
                  firmwareAsDesiredResult.status === 'Loading',
                reason: disabled
                  ? 'This firmware is not eligible for rollout.'
                  : !isValidSerialNumberList
                    ? (validationMessage ??
                      'Enter valid device serial numbers.')
                    : 'A request is already being submitted.',
              },
            ]}
          />
        </div>
      </div>
      {validationMessage && !disabled && (
        <div
          id={validationId}
          className="text-xs text-red-600 mt-2"
          role="alert"
        >
          {validationMessage}
        </div>
      )}
      <StatusField
        result={firmwareAsDesiredResult}
        successMessage="Desired firmware saved. Device upgrade outcome is not yet confirmed."
      />
    </div>
  );
};
